import React, { useEffect, useReducer } from 'react';
import { Row, Col } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { Currency } from '../lib/components/Currency';
import { WithApiAccess } from "../lib/HigherOrderComponents/WithApiAccess";
import { LoadingSpinner } from '../lib/components/LoadingSpinner';
import { withHeaderMessages } from '../higherOrderComponents/WithHeaderMessages';
import { ContractLayout } from './ContractLayout';
import spinner from '../static/images/spinner.gif';
import qs from 'qs';

const CombinedDetails = (props) => {
    
    const location = useLocation();
    const {
        n
    } = qs.parse(location.search, { ignoreQueryPrefix: true });

    const initialState = {
        isLoading: true,
        contracts: [],
        payoffsAll: [],
        payoffsLoading: true,
        contractsQueryString: n
    }

    const reducer = (state, action) => {
        switch (action.type) {
            case 'contracts':
                return {
                    ...state,
                    contracts: action.payload,
                    isLoading: false
                }
            case 'payoffsAll':
                return {
                    ...state,
                    payoffsAll: action.payload,
                    payoffsLoading: false
                }
            case 'payoffsLoading':
                return {
                    ...state,
                    payoffsLoading: action.payload
                }
            case 'contractsQueryString':
                return {
                    ...state,
                    contractsQueryString: action.payload,
                }
            case 'hideLoader': 
                return {
                    ...state,
                    isLoading: false
                }
            default:
                throw new Error();
        }
    }

    const [combinedDetailsState, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        async function getContractsDetail(){
            const { contractsQueryString } = combinedDetailsState;
            props.header.setHeader("combinedDetails");
            const contractNumbers = contractsQueryString.split(",") || [];
            
            Promise.all(
                contractNumbers.map(n => 
                    props.api.getContract(`/api/contract/${n}`)
                    .then(response => {
                        if (response.status === 200) { 
                            return response.data;
                        }
                    })
                )
            )
            .catch(() => dispatch({ type: 'isLoading', payload: false }))
            .then(contracts => {
                dispatch({ type: 'contracts', payload: contracts });
            });
        }
        getContractsDetail()
    }, [])

    useEffect(() => {
        async function getPayoffDetails() {
            const { contractsQueryString } = combinedDetailsState;
            const contractNumbers = contractsQueryString.split(",") || [];
            Promise.all(
                contractNumbers.map(n => 
                    props.api.getContract(`/api/contract/${n}/payoff`)
                    .then(response => {
                        if (response.status === 200) { 
                            return response.data;
                        }
                    })
                )
            )
            .catch(() => dispatch({ type: 'payoffsLoading', payload: false }))
            .then(payoffs => {
                dispatch({ type: 'payoffsAll', payload: payoffs.flatMap(p => p)});
            });
        }
        getPayoffDetails();
    }, [])

    const renderContractPurchases = (contract) => {
        const purchases = contract.purchases
        return (
            purchases.map((p, i) => {
                return (
                    <tr key={i}>
                        <td>{contract.provider.name}</td>
                        <td>{p.providerNumber}</td>
                        <td><Link to={{
                                pathname:`/contracts/${p.contractNumber}`,
                                }}>{p.contractNumber}
                            </Link>
                        </td>
                        <td>{p.itemNumber} {p.description}</td>
                        <td>{p.currentStatus}</td>
                        <td style={{textAlign: "right"}}><Currency amount={p.netPrice} /></td>
                        <td style={{textAlign: "right"}}><Currency amount={p.totalTrusted + p.totalRetained + p.disbursedGuaranteedPrincipal + p.disbursedNonGuaranteedPrincipal} /></td>
                        <td style={{textAlign: "right"}}><Currency amount={(p.totalToRetain - p.totalRetained) + (p.totalToTrust - p.totalTrusted)} /></td>
                        <td style={{textAlign: "right"}}><Currency amount={p.totalTrusted} /></td>
                        <td style={{textAlign: "right"}}><Currency amount={p.totalRetained} /></td>
                        <td style={{textAlign: "right"}}><Currency amount={p.income} /></td>
                        <td style={{textAlign: "right"}}><Currency amount={p.totalTrusted + p.income} /></td>
                    </tr>)
            })
        )
    }

    const renderContractTotal = (contract) => {
        const { payoffsAll, payoffsLoading } = combinedDetailsState;
        const payoff = payoffsAll.find(p => p.contractNumber === contract.number);
        const purchases = contract.purchases;
        const planTotalTotal = purchases.map(p => p.netPrice).reduce((a, b) => a + b, 0);
        const amountPaidTotal = purchases.map(p => p.totalTrusted + p.totalRetained + p.disbursedGuaranteedPrincipal + p.disbursedNonGuaranteedPrincipal).reduce((a, b) => a + b, 0);
        const balanceDueTotal = purchases.map(p => (p.totalToRetain - p.totalRetained) + (p.totalToTrust - p.totalTrusted)).reduce((a, b) => a + b, 0);
        const totalTrustedTotal = purchases.map(p => p.totalTrusted).reduce((a, b) => a + b, 0);
        const totalRetainedTotal = purchases.map(p => p.totalRetained).reduce((a, b) => a + b, 0);
        const totalIncomeTotal = purchases.map(p => p.income).reduce((a, b) => a + b, 0);
        const currentValueTotal = purchases.map(p => p.totalTrusted + p.income).reduce((a, b) => a + b, 0);
        return (
            <>
                <tr>
                    <td style={{fontWeight: "bold", textAlign: "right"}} colSpan="4">Total</td>
                    <td></td>
                    <td style={{fontWeight: "bold", textAlign: "right"}}><Currency amount={planTotalTotal} /></td>
                    <td style={{fontWeight: "bold", textAlign: "right"}}>
                        <Currency amount={amountPaidTotal} />
                        { (contract.nonWaivedNonKeptProviderFees && contract.nonWaivedNonKeptProviderFees !== 0) &&
                            <span>~</span>
                        }
                    </td>
                    <td style={{fontWeight: "bold", textAlign: "right"}}><Currency amount={balanceDueTotal} /></td>
                    <td style={{fontWeight: "bold", textAlign: "right"}}><Currency amount={totalTrustedTotal} /></td>
                    <td style={{fontWeight: "bold", textAlign: "right"}}><Currency amount={totalRetainedTotal} /></td>
                    <td style={{fontWeight: "bold", textAlign: "right"}}><Currency amount={totalIncomeTotal} /></td>
                    <td style={{fontWeight: "bold", textAlign: "right"}}><Currency amount={currentValueTotal} /></td>
                </tr>
                <tr>
                    <td colSpan="7" style={{fontWeight: "bold", textAlign: "right"}}>Payoff Finance Amount</td>
                    <td style={{fontWeight: "bold", textAlign: "right"}}>
                        { payoffsLoading &&
                            <span>Loading...<img src={spinner} alt="Loading ..." /></span>
                        }
                        { !payoffsLoading && payoff &&
                            <Currency amount={payoff.payoffFinanceChargeAmount} />
                        }
                    </td>
                </tr>
                <tr>
                    <td colSpan="7" style={{fontWeight: "bold", textAlign: "right"}}>Payoff Amount</td>
                    <td style={{fontWeight: "bold", textAlign: "right"}}>
                        { payoffsLoading &&
                            <span>Loading...<img src={spinner} alt="Loading ..." /></span>
                        }
                        { !payoffsLoading && payoff &&
                            <Currency amount={payoff.payoffAmount} />
                        }
                    </td>
                </tr>
                <tr>
                    <td colSpan="12" style={{fontSize: "small", textAlign: "center", paddingBottom: "20px"}}>
                        { (contract.nonWaivedNonKeptProviderFees && contract.nonWaivedNonKeptProviderFees !== 0) &&
                            <div>~ Excludes Finance Charges and/or other Provider Fees of <Currency amount={contract.nonWaivedNonKeptProviderFees} /> as they are not item specific</div>
                        }
                    </td>
                </tr>
                <tr><td></td></tr>
            </>
        )
    }

    const renderContract = (contract) => {
        return (
            <>
                {renderContractPurchases(contract)}
                {renderContractTotal(contract)}
            </>
        )
    }

    const renderAmounts = (providerPurchases, status) => {
        const purchases = providerPurchases.filter(p => p.currentStatus === status);
        const planTotalTotal = purchases.map(p => p.netPrice).reduce((a, b) => a + b, 0);
        const amountPaidTotal = purchases.map(p => p.totalTrusted + p.totalRetained + p.disbursedGuaranteedPrincipal + p.disbursedNonGuaranteedPrincipal).reduce((a, b) => a + b, 0);
        const balanceDueTotal = purchases.map(p => (p.totalToRetain - p.totalRetained) + (p.totalToTrust - p.totalTrusted)).reduce((a, b) => a + b, 0);
        const totalTrustedTotal = purchases.map(p => p.totalTrusted).reduce((a, b) => a + b, 0);
        const totalRetainedTotal = purchases.map(p => p.totalRetained).reduce((a, b) => a + b, 0);
        const totalIncomeTotal = purchases.map(p => p.income).reduce((a, b) => a + b, 0);
        const currentValueTotal = purchases.map(p => p.totalTrusted + p.income).reduce((a, b) => a + b, 0);
        return (
            <tr className="font-bold">
                <td colSpan="3"></td>
                <td colSpan="2" style={{paddingLeft: "15px"}}>{status}</td>
                <td style={{textAlign: "right"}}><Currency amount={planTotalTotal} /></td>
                <td style={{textAlign: "right"}}>
                    <Currency amount={amountPaidTotal} />
                    {/* { (includeFootnote === true) &&
                        <span>~</span>
                    } */}
                </td>
                <td style={{textAlign: "right"}}><Currency amount={balanceDueTotal} /></td>
                <td style={{textAlign: "right"}}><Currency amount={totalTrustedTotal} /></td>
                <td style={{textAlign: "right"}}><Currency amount={totalRetainedTotal} /></td>
                <td style={{textAlign: "right"}}><Currency amount={totalIncomeTotal} /></td>
                <td style={{textAlign: "right"}}><Currency amount={currentValueTotal} /></td>
            </tr>
        )
    }

    const renderCombinedTotals = (contracts) => {
        const { payoffsAll, payoffsLoading } = combinedDetailsState;
        const purchases = contracts
            .flatMap(c => c.purchases)
            .map(p => {
                const contract = contracts.find(c => c.number === p.contractNumber);
                return { 
                    ...p,
                    providerName: contract.provider.name,
                    providerNumber: contract.providerNumber }
            });
        const payoffFinanceChargeAmount = payoffsAll.map(p => p.payoffFinanceChargeAmount).reduce((a, b) => a + b, 0);
        const payoffAmount = payoffsAll.map(p => p.payoffAmount).reduce((a, b) => a + b, 0);
        const statuses = [...new Set(purchases.map(p => p.currentStatus))];
        const nonWaivedNonKeptProviderFees = contracts.map(c => c.nonWaivedNonKeptProviderFees || 0).reduce((a, b) => a + b, 0);

        return (
            <>
                <tr className="font-bold">
                    <td colSpan="3"></td>
                    <td colSpan="8">Combined Totals</td>
                </tr>
                {statuses.map(s => renderAmounts(purchases, s))}
                <tr className="font-bold">
                    <td colSpan="7" style={{textAlign: "right"}}>Payoff Finance Amount</td>
                    <td style={{textAlign: "right"}}>
                        { payoffsLoading &&
                            <span>Loading...<img src={spinner} alt="Loading ..." /></span>
                        }
                        { !payoffsLoading &&
                            <Currency amount={payoffFinanceChargeAmount} />
                        }
                    </td>
                </tr>
                <tr className="font-bold">
                    <td colSpan="7" style={{textAlign: "right"}}>Payoff Amount</td>
                    <td style={{textAlign: "right"}}>
                        { payoffsLoading &&
                            <span>Loading...<img src={spinner} alt="Loading ..." /></span>
                        }
                        { !payoffsLoading &&
                            <Currency amount={payoffAmount} />
                        }
                    </td>
                </tr>
                <tr>
                    <td colSpan="12" style={{fontSize: "small", textAlign: "center"}}>
                        { (nonWaivedNonKeptProviderFees !== 0) &&
                            <div>Combined Totals Amount Paid values above exclude Finance Charges and/or other Provider Fees of <Currency amount={nonWaivedNonKeptProviderFees} /></div>
                        }
                    </td>
                </tr>
                <tr><td></td></tr>
            </>)
    }

    if (combinedDetailsState.isLoading) {
        return <LoadingSpinner />;
    }

    const { contracts, contractsQueryString } = combinedDetailsState;
    const sortedContracts = contracts.sort((a, b) => a.provider.name < b.provider.name ? -1 : 1);
    const status = sortedContracts.some(c => c.status === "Accepted") ? "Accepted" : "";

    return (
        <ContractLayout contractNumber={contractsQueryString} isCombined={true}
            contractStatus={status} displayButtons={true}>
            <Row className="page-break my-4"> </Row>
            <Row>
                <Col sm={12}>
                    <table className="table table-sm dq3-table">
                        <thead>
                            <tr>
                                <th style={{fontWeight: "bold"}}>Provider Name</th>
                                <th style={{fontWeight: "bold"}}>Provider Contract #</th>
                                <th style={{fontWeight: "bold"}}>ClearPoint Contract #</th>
                                <th style={{fontWeight: "bold"}}>Item Number &amp; Description</th>
                                <th style={{fontWeight: "bold"}}>Status</th>
                                <th style={{fontWeight: "bold", textAlign: "right"}}>Plan Total</th>
                                <th style={{fontWeight: "bold", textAlign: "right"}}>Amount Paid</th>
                                <th style={{fontWeight: "bold", textAlign: "right"}}>Balance Due</th>
                                <th style={{fontWeight: "bold", textAlign: "right"}}>Total Trusted</th>
                                <th style={{fontWeight: "bold", textAlign: "right"}}>Total Retained</th>
                                <th style={{fontWeight: "bold", textAlign: "right"}}>Total Income</th>
                                <th style={{fontWeight: "bold", textAlign: "right"}}>Current Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedContracts.map(contract => renderContract(contract))}
                            <tr><td></td></tr>
                            {renderCombinedTotals(sortedContracts)}
                        </tbody>
                    </table>
                </Col>
            </Row>
            <Row>
                <Col>
                    <p style={{marginBottom: "25px", color: "#6C6C6D"}}>
                        Details Disclosure: Item values are based on all activity processed through the previous day;
                            the amount is subject to change based on additional trust activity including income, investment gains, and fees.
                            Payoff finance amount and payoff amounts may differ from item totals due to current day payments.
                    </p>
                </Col>
            </Row>
        </ContractLayout>
    )
}

export default withHeaderMessages(WithApiAccess(CombinedDetails));
